'use client'

import '../../../i18n/client'
import React, { useCallback, useEffect } from 'react'
import { graphql } from '@/generated/gql/gql'
import { useCcMenuState } from './components/state/useCcMenuState'
import { useFragment } from '@/generated/gql/fragment-masking'
import LoadingIndicator from '@/components/LoadingIndicator'
import MenuView from './components/MenuView'
import { useLocale } from '@/components/Link'
import { getLangFromLocale } from '@/helpers/multiRegion'
import {
  CCMenuUserStateCustomError,
  CCMenuUserStateCustomErrorCode,
  useCcMenuUserState,
} from './components/state/useCcMenuUserState'
import { useDefaultCurrency } from './components/state/useDefaultCurrency'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import moment from 'moment'
import { Language } from '@/generated/gql/graphql'
import { useCcMenuFiltersState } from './components/state/useCcMenuFiltersState'
import CanteenError from './components/CanteenError'

const canteenMenuCurrentMenuDataFragment = graphql(`
  fragment CanteenMenu_CurrentMenuDataFragment on CCMealService {
    ...CanteenMenuView_CurrentMenuDataFragment
  }
`)

const canteenMenuUserDataFragment = graphql(`
  fragment CanteenMenu_UserDataFragment on User {
    ...UseCanteenMenuFiltersState_UserDataFragment
    ...UseCanteenMenuState_UserDataFragment
    ...CanteenAccountLocation_UserDataFragment
    ...CanteenMenuView_UserDataFragment
  }
`)

const Body: React.FC = () => {
  const locale = useLocale()
  const params = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const paramsDate = params?.get('date') ?? ''
  const paramsMeal = params?.get('meal') ?? ''
  const paramsTimeslot = params?.get('timeslot') ?? ''
  const paramsVendors = params?.get('vendors') ?? ''
  const paramsDietaries = params?.get('dietaries') ?? ''
  const paramsAllergens = params?.get('allergens') ?? ''

  const {
    updateAccountLocation,
    loading: loadingUserData,
    data: userDataProp,
    error: userDataError,
    called: userDataCalled,
    refetch: refetchUserData,
  } = useCcMenuUserState()
  const userData = useFragment(canteenMenuUserDataFragment, userDataProp)

  const { filters, toggleFilterValue, clearFilters } = useCcMenuFiltersState({
    userData,
    paramsAllergens,
    paramsVendors,
    paramsDietaries,
  })

  const {
    date,
    meal,
    timeslot,
    loading: loadingMenuData,
    menuData: menuDataProp,
    menuClosed,
    error: menuDataError,
    orderTimeRemaining,
    refetch: refetchMenuData,
    updateSelectedService,
    updateTimeslot,
  } = useCcMenuState({ paramsDate, paramsMeal, paramsTimeslot, userData })
  const menuData = useFragment(canteenMenuCurrentMenuDataFragment, menuDataProp)

  const createQueryString = useCallback(
    (values: Record<string, string | number>) => {
      const paramsToSet = new URLSearchParams(params?.toString())
      Object.entries(values).forEach(([key, value]) => {
        if (value === '') paramsToSet.delete(key)
        else paramsToSet.set(key, value.toString())
      })
      return paramsToSet.toString()
    },
    [params],
  )

  useEffect(() => {
    if (userDataError?.name === CCMenuUserStateCustomErrorCode.AccountNotCCActive) {
      router.push('/office-catering/vendors')
    }
    if (userDataError?.name === CCMenuUserStateCustomErrorCode.SelectedLocationNotCCActive) {
      router.push('/authentication/cloud-canteen-signup-finish?destination=/canteen')
    }
  }, [userDataError, router])

  useEffect(() => {
    if (userDataError) return
    if (!date && !meal && !timeslot) return
    const newParams = {
      ...(timeslot &&
        paramsTimeslot !== timeslot && { timeslot: moment(timeslot).format('HH:mm') }),
      ...(meal && paramsMeal !== meal && { meal }),
      ...(date && paramsDate !== date && { date }),
      ...(filters.allergens.length
        ? { allergens: filters.allergens.join(',') }
        : { allergens: '' }),
      ...(filters.dietaries.length
        ? { dietaries: filters.dietaries.join(',') }
        : { dietaries: '' }),
      ...(filters.vendors.length ? { vendors: filters.vendors.join(',') } : { vendors: '' }),
    }
    if (Object.keys(newParams).length === 0) return
    const queryString = createQueryString(newParams)
    router.push(`${pathname}?${queryString}`)
  }, [
    date,
    meal,
    timeslot,
    paramsTimeslot,
    paramsDate,
    paramsMeal,
    router,
    pathname,
    createQueryString,
    filters.allergens,
    filters.vendors,
    filters.dietaries,
    userDataError,
  ])

  if (!locale) throw new Error('LOCALE_REQUIRED')

  const currencyForLocaleCountry = useDefaultCurrency(userData)

  const isInitialLoad =
    !userDataCalled || (loadingUserData && !userData) || (loadingMenuData && (!meal || !date))

  const hasErrors = Boolean(userDataError || menuDataError)

  const reload = useCallback(async () => {
    await refetchUserData()
    await refetchMenuData()
  }, [refetchMenuData, refetchUserData])

  const onOrderPlacement = useCallback(
    ({ success, forwardUrl }: { success: boolean; forwardUrl: string }) => {
      if (success && forwardUrl) router.push(`${forwardUrl}`)
      else {
        reload()
      }
    },
    [reload, router],
  )
  if (
    hasErrors &&
    !(userDataError as CCMenuUserStateCustomError)?.redirect &&
    (userDataError?.message || menuDataError?.message)
  )
    return (
      <CanteenError
        message={userDataError?.message ?? menuDataError?.message}
        code={userDataError?.name ?? menuDataError?.name}
      />
    )
  if (isInitialLoad) return <LoadingIndicator />
  return (
    <>
      {userData && !userDataError && !menuDataError && date && meal ? (
        <MenuView
          onClearFilters={clearFilters}
          menuFilters={filters}
          onToggleFilterValue={toggleFilterValue}
          language={getLangFromLocale(locale) ?? Language.En}
          orderTimeRemaining={orderTimeRemaining}
          menuClosed={menuClosed}
          onChangeTimeslot={updateTimeslot}
          onChangeAccountLocation={(accountLocationId) => {
            updateAccountLocation(accountLocationId)
          }}
          loadingSelectedServiceData={loadingMenuData}
          onChangeSelectedService={updateSelectedService}
          currency={currencyForLocaleCountry}
          meal={meal}
          date={date}
          timeslot={timeslot}
          menuData={menuData}
          userData={userData}
          onOrderPlacement={onOrderPlacement}
          reloadFromApi={reload}
        />
      ) : null}
    </>
  )
}

export default Body
